import { useEffect, useState } from 'react';

/**
 * Whether asking for location again would actually prompt, or do nothing.
 *
 * `PERMISSION_DENIED` from the Geolocation API covers two different situations and cannot tell them
 * apart: the student DISMISSED the prompt, which is recoverable by asking again from a fresh
 * gesture, and the student DENIED it, which the browser remembers per origin and no API can undo.
 * Both arrive as `err.code === 1`, so without this a student one tap from fixing it gets the same
 * sentence as one who has to go into browser settings — beside a retry button that does nothing.
 *
 * The Permissions API is the only thing that separates them. `'prompt'` means the grant was never
 * made, so asking again works; `'denied'` means it was refused and stored.
 *
 * NOT available for the motion sensors Compass and Angle use: `DeviceMotionEvent.requestPermission`
 * is an Apple extension with no Permissions API name, so those two always offer retry and say to
 * check settings if it keeps failing. This is the one sensor that can do better, so it does.
 */
export type GeolocationPermission = 'granted' | 'denied' | 'prompt' | 'unknown';

export function useGeolocationPermission(active: boolean): GeolocationPermission {
  const [state, setState] = useState<GeolocationPermission>('unknown');

  useEffect(() => {
    if (!active) return undefined;
    // Feature-detected rather than assumed: Safari only shipped `permissions.query` for geolocation
    // relatively late, and an older one throws on the unrecognised name rather than resolving.
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return undefined;

    let status: PermissionStatus | null = null;
    let cancelled = false;
    const onChange = () => { if (status && !cancelled) setState(status.state); };

    navigator.permissions.query({ name: 'geolocation' })
      .then((s) => {
        if (cancelled) return;
        status = s;
        setState(s.state);
        // Live, so a student who fixes it in browser settings sees the message go without a reload.
        s.addEventListener('change', onChange);
      })
      .catch(() => { /* Unsupported name, or blocked by policy. 'unknown' is the honest answer. */ });

    return () => {
      cancelled = true;
      status?.removeEventListener('change', onChange);
    };
  }, [active]);

  return state;
}
